'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

interface CurrencyContextType {
  currency: string;
  setCurrency: (currency: string) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Mock country-currency map
const countryCurrencyMap: Record<string, string> = {
  India: 'INR',
  'United States': 'USD',
  'United Arab Emirates': 'AED',
  // add more as needed
};

export const CurrencyProvider = ({ children }: any) => {
  const [currency, setCurrency] = useState('USD');
  const [cookieAllowed, setCookieAllowed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const storedConsent = localStorage.getItem('cookie_consent');
    const storedCurrency = localStorage.getItem('currency');

    if (storedConsent === 'true') {
      setCookieAllowed(true);
    } else {
      setShowBanner(true); // Show banner if no consent yet
    }

    if (storedCurrency) {
      setCurrency(storedCurrency);
    }
  }, []);

  useEffect(() => {
    if (cookieAllowed) {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
              );
              const data = await response.json();
              const country = data.countryName;

              if (countryCurrencyMap.hasOwnProperty(country)) {

                const currencyFromCountry = countryCurrencyMap[country];
                setCurrency(currencyFromCountry);
                localStorage.setItem('currency', currencyFromCountry);
              } else {
                setCurrency('USD');
                localStorage.setItem('currency', 'USD');
              }
            } catch (error) {
              console.error('Error getting location:', error);
              setCurrency('USD');
              localStorage.setItem('currency', 'USD');
            }
          },
          (error) => {
            console.error('Error getting location:', error);
            setCurrency('USD');
            localStorage.setItem('currency', 'USD');
          }
        );
      } else {
        setCurrency('USD');
        localStorage.setItem('currency', 'USD');
      }
    }
  }, [cookieAllowed]);

  const handleAllowCookies = () => {
    localStorage.setItem('cookie_consent', 'true');
    setCookieAllowed(true);
    setShowBanner(false);
  };
  const handleDenyCookies = () => {
    localStorage.setItem('cookie_consent', 'false');
    localStorage.setItem('currency', 'USD');
    setCurrency('USD');
    setCookieAllowed(false);
    setShowBanner(false);
  };


  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      {showBanner && (
  <div
    className="
      fixed bottom-0 left-0 right-0 w-full
      bg-white border-t border-gray-200 shadow-md z-50
      px-4 sm:px-8 py-2
    "
  >
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between max-w-7xl mx-auto  ">
      <span className="text-sm sm:text-base text-gray-800 font-medium text-center sm:text-left ">
        We use cookies to personalize currency settings for a better shopping experience. Please allow cookies to continue.

      </span>
      <div className="mt-3 sm:mt-0 flex justify-center sm:justify-end gap-3">
        <button
          className="px-5 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
          onClick={handleDenyCookies}
        >
          Deny
        </button>
        <button
          className="px-5 py-2 text-sm font-semibold text-white bg-black rounded-md hover:bg-gray-900 transition"
          onClick={handleAllowCookies}
        >
          Allow
        </button>
      </div>
    </div>
  </div>
)}




      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
